/**
 * The migration centre.
 *
 * ═══════════════════════════════════════════════════════════════════
 * NOTHING IS EVER SILENTLY DISCARDED.
 *
 * Replacing a finance system that has been running for decades means moving
 * data that nobody fully understands any more. The one unforgivable behaviour
 * is a migration that drops what it cannot parse and reports success — the loss
 * is discovered at the first year end, by which time the old system is gone.
 *
 * So every row is accounted for: accepted, or held as an exception with the
 * reason. A batch whose control totals do not reconcile to the source system's
 * own figures cannot be approved for import. The pipeline is: upload, inspect,
 * map, validate, dry run, reconcile, approve, import.
 * ═══════════════════════════════════════════════════════════════════
 *
 * This screen carries that pipeline rather than describing it. All eight steps
 * are controls here, and a control is only drawn when the server would actually
 * accept it — so "Approve for import" is absent, with the reasons listed
 * beneath, rather than present and then refused. The one control the server
 * will not let the uploader use is the approval, because a second person is the
 * point of it; that refusal is explained on screen instead of being discovered
 * by clicking.
 *
 * The field names below follow the migration_batch table exactly — data_domain,
 * valid_count, error_count, declared_control_total_minor and the rest. The
 * server is the contract: where a name here disagrees with the wire, the name
 * here is the one that is wrong.
 */

import { useState, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api } from '../lib/api';
import { useAsync, useMutation } from '../lib/useAsync';
import { useSession } from '../lib/session';
import { lower, useTerms } from '../lib/tenant';
import { dateTime, humanise, money, number, statusLabel, statusTone } from '../lib/format';
import {
  Alert,
  AsyncPanel,
  Badge,
  Card,
  DataTable,
  DefinitionList,
  EmptyState,
  ErrorNotice,
  Modal,
  Money,
  PageHeader,
  Select,
  Stat,
  Tabs,
  TextArea,
  TextInput,
} from '../components/ui';

/*
 * The BIGINT control totals arrive as strings from a real PostgreSQL driver and
 * as numbers from some pooled ones, so every one of them goes through Number()
 * at the point of use rather than being trusted to be either.
 */
type MinorUnits = number | string;

interface Batch {
  id: string;
  reference: string;
  name: string;
  source_system: string;
  data_domain: string;
  status: string;
  filename: string | null;
  row_count: number;
  valid_count: number;
  warning_count: number;
  error_count: number;
  imported_count: number;
  declared_control_total_minor: MinorUnits | null;
  declared_record_count: number | null;
  computed_control_total_minor: MinorUnits;
  reconciliation_difference_minor: MinorUnits;
  reconciled: boolean;
  field_mapping: Record<string, string>;
  detected_columns: string[];
  dry_run_at: string | null;
  dry_run_summary: { create: number; update: number; skip: number; blockers: string[] } | null;
  approved_at: string | null;
  imported_at: string | null;
  created_by: string;
  created_at: string;
  notes: string | null;
  created_by_name: string | null;
  approved_by_name: string | null;
}

interface FieldSpec {
  key: string;
  label: string;
  required: boolean;
  type: string;
  description: string;
  options?: string[];
  aliases: string[];
}

interface Domain {
  key: string;
  label: string;
  description: string;
  fields: FieldSpec[];
}

interface WorkflowStep {
  step: number;
  name: string;
  description: string;
}

interface RowFinding {
  severity: 'error' | 'warning' | 'info';
  field: string | null;
  message: string;
}

interface SourceRow {
  row_number: number;
  raw_data: Record<string, unknown>;
  mapped_data: Record<string, unknown> | null;
  status: string;
  findings: RowFinding[];
  created_record_table: string | null;
  created_record_id: string | null;
}

/** An exception is a source row that was kept back, with the reason it was kept. */
interface Exception {
  row_number: number;
  raw_data: Record<string, unknown>;
  findings: RowFinding[];
}

/** GET /migration/batches/:id */
interface BatchDetail {
  batch: Batch;
  rows: SourceRow[];
  targetFields: FieldSpec[];
  domainLabel: string;
}

/** GET /migration/batches/:id/reconciliation */
interface BatchReconciliation {
  batch: Batch;
  rowOutcomes: Array<{ status: string; count: number }>;
  exceptions: Exception[];
  reconciliation: { declaredMinor: number | null; computedMinor: number; differenceMinor: number; reconciled: boolean };
  statement: string;
}

/** The two detail payloads, merged, so the panel has one loading and one failure state. */
type Detail = BatchReconciliation & BatchDetail;

interface UploadResult {
  batchId: string;
  reference: string;
  mapping: Record<string, string>;
  headers: string[];
  rowCount: number;
  unmappedFields: Array<{ key: string; label: string; required: boolean }>;
}

interface ValidationSummary {
  rowCount: number;
  validCount: number;
  warningCount: number;
  errorCount: number;
  computedControlTotalMinor: number;
}

interface DryRunResult {
  wouldCreate: number;
  wouldUpdate: number;
  wouldSkip: number;
  blockers: string[];
}

interface ImportResult {
  imported: number;
  skipped: number;
  reference: string;
}

/* ------------------------------------------------------------------ *
 * The state machine, said in the words the room uses
 * ------------------------------------------------------------------ */

/** How far along the eight published steps each batch status has reached. */
const STEP_REACHED: Record<string, number> = {
  uploaded: 1,
  inspected: 2,
  mapped: 3,
  validated: 4,
  dry_run: 5,
  reconciled: 6,
  pending_approval: 6,
  approved: 7,
  importing: 7,
  imported: 8,
};

/** Statuses at which the batch has not been checked against its target shape yet. */
const AWAITING_VALIDATION = ['uploaded', 'inspected', 'mapped'];

/** The only statuses from which the server will accept an approval. */
const APPROVABLE = ['validated', 'dry_run', 'reconciled'];

/** Past the point where the mapping may still be changed. */
const SETTLED = ['approved', 'importing', 'imported', 'failed', 'cancelled'];

/**
 * What each row outcome means to a reader.
 *
 * statusLabel() would render these as "Valid" and "Error", which are the
 * database's words for them. The promise this screen makes is that a row is
 * either accepted or held with its reason, so those are the words used.
 */
const ROW_OUTCOMES: Record<
  string,
  { label: string; plural: string; tone: 'neutral' | 'ok' | 'warn' | 'error' | 'info' }
> = {
  pending: { label: 'Not yet validated', plural: 'not yet validated', tone: 'neutral' },
  valid: { label: 'Accepted', plural: 'accepted', tone: 'ok' },
  warning: { label: 'Accepted with a warning', plural: 'accepted with warnings', tone: 'warn' },
  error: { label: 'Held as an exception', plural: 'held as exceptions', tone: 'error' },
  skipped: { label: 'Not imported', plural: 'not imported', tone: 'neutral' },
  rejected: { label: 'Rejected', plural: 'rejected', tone: 'error' },
  imported: { label: 'Imported', plural: 'imported', tone: 'ok' },
};

const rowOutcome = (status: string) =>
  ROW_OUTCOMES[status] ?? { label: statusLabel(status), plural: lower(statusLabel(status)), tone: 'neutral' as const };

/** "12 accepted · 2 held as exceptions" — the whole batch, reconciled to its row count. */
const outcomeSummary = (outcomes: Array<{ status: string; count: number }>): string =>
  outcomes
    .map((o) => `${number(o.count)} ${o.count === 1 ? lower(rowOutcome(o.status).label) : rowOutcome(o.status).plural}`)
    .join(' · ');

const asMinor = (value: MinorUnits | null | undefined): number | null =>
  value === null || value === undefined ? null : Number(value);

/** A batch shows counts only once it has actually been counted. */
const hasBeenValidated = (b: Batch): boolean => !AWAITING_VALIDATION.includes(b.status);

/**
 * Why the server would refuse to approve this batch.
 *
 * Computed here from the batch record rather than read from the stored dry-run
 * summary, so the reasons stay true after a re-validation and are available
 * before a dry run has ever been made.
 */
function approvalBlockers(b: Batch): string[] {
  const reasons: string[] = [];
  if (!APPROVABLE.includes(b.status)) {
    reasons.push('Every row has to be validated and a dry run completed before the batch can be approved.');
  }
  if (b.error_count > 0) {
    reasons.push(
      `${number(b.error_count)} ${b.error_count === 1 ? 'row is' : 'rows are'} held as ` +
        `${b.error_count === 1 ? 'an exception' : 'exceptions'}. Correct them in the source system and upload the extract again.`,
    );
  }
  if (b.declared_control_total_minor !== null && !b.reconciled) {
    reasons.push(
      `The total computed from the rows, ${money(asMinor(b.computed_control_total_minor))}, does not agree with the ` +
        `${money(asMinor(b.declared_control_total_minor))} declared from the source system — a difference of ` +
        `${money(Math.abs(Number(b.reconciliation_difference_minor)))}.`,
    );
  }
  return reasons;
}

/**
 * The required-field marker, outside a form label.
 *
 * The asterisk is decoration; the word is what a screen reader needs, which is
 * how the shared Field component treats it too.
 */
function RequiredMark() {
  return (
    <>
      <span className="field__required" aria-hidden="true">
        {' '}
        *
      </span>
      <span className="visually-hidden"> (required)</span>
    </>
  );
}

/** A source row, laid out exactly as it arrived, in the file's own column order. */
function rowAsSupplied(columns: string[], raw: Record<string, unknown>): string {
  const width = columns.reduce((w, c) => Math.max(w, c.length), 0);
  return columns
    .map((c) => {
      const value = raw[c];
      const text = value === null || value === undefined || value === '' ? '(empty)' : String(value);
      return `${c.padEnd(width)}   ${text}`;
    })
    .join('\n');
}

const EMPTY_UPLOAD = {
  name: '',
  sourceSystem: '',
  dataDomain: '',
  declaredControlTotal: '',
  declaredRecordCount: '',
  notes: '',
};

export function MigrationPage() {
  const { can, user } = useSession();
  const t = useTerms();

  // The chosen batch lives in the address bar so a link to a particular
  // migration survives a reload and can be sent to whoever has to approve it.
  const [searchParams, setSearchParams] = useSearchParams();

  const batches = useAsync(() => api.get<{ batches: Batch[] }>('/integrations/migration/batches'), []);
  const domains = useAsync(
    () => api.get<{ domains: Domain[]; workflow: WorkflowStep[] }>('/integrations/migration/domains'),
    [],
  );

  const list = batches.data?.batches ?? [];
  // The page opens on the most recent batch rather than on an empty panel: a
  // migration centre with nothing selected answers none of the questions it
  // exists to answer.
  const selected = searchParams.get('batch') ?? list[0]?.id ?? null;

  const [notice, setNotice] = useState<{ tone: 'ok' | 'info' | 'warn'; title: string; body: ReactNode } | null>(null);
  const [tabOverride, setTabOverride] = useState<string | null>(null);
  const [draftMapping, setDraftMapping] = useState<{ batchId: string; mapping: Record<string, string> } | null>(null);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadForm, setUploadForm] = useState(EMPTY_UPLOAD);
  const [file, setFile] = useState<File | null>(null);
  const [confirmImport, setConfirmImport] = useState<Batch | null>(null);

  const select = (id: string) => {
    setSearchParams({ batch: id }, { replace: true });
    setNotice(null);
    setTabOverride(null);
    setDraftMapping(null);
  };

  /*
   * Both detail payloads are fetched together. The batch record from
   * /batches/:id carries the uploader's and approver's names; the one inside the
   * reconciliation payload does not, so the fuller of the two is spread last.
   */
  const detail = useAsync(
    () =>
      selected
        ? Promise.all([
            api.get<BatchReconciliation>(`/integrations/migration/batches/${selected}/reconciliation`),
            api.get<BatchDetail>(`/integrations/migration/batches/${selected}`),
          ]).then(([reconciliation, batch]): Detail => ({ ...reconciliation, ...batch }))
        : Promise.resolve(null as never),
    [selected],
  );

  const upload = useMutation((form: FormData) => api.upload<UploadResult>('/integrations/migration/batches', form));
  const saveMapping = useMutation((id: string, mapping: Record<string, string>) =>
    api.put<{ ok: boolean }>(`/integrations/migration/batches/${id}/mapping`, { mapping }),
  );
  const validate = useMutation((id: string) =>
    api.post<ValidationSummary>(`/integrations/migration/batches/${id}/validate`),
  );
  const dryRun = useMutation((id: string) => api.post<DryRunResult>(`/integrations/migration/batches/${id}/dry-run`));
  const approve = useMutation((id: string) =>
    api.post<{ reference: string }>(`/integrations/migration/batches/${id}/approve`),
  );
  const runImport = useMutation((id: string) => api.post<ImportResult>(`/integrations/migration/batches/${id}/import`));

  const refresh = () => {
    batches.reload();
    detail.reload();
  };

  /* ---------------------------------------------------------------- *
   * The eight steps, as actions
   * ---------------------------------------------------------------- */

  const doValidate = (b: Batch) =>
    void validate.run(b.id).then((summary) => {
      if (!summary) return;
      setNotice({
        tone: summary.errorCount > 0 ? 'warn' : 'ok',
        title: `${b.reference} checked: ${number(summary.rowCount)} rows`,
        body:
          summary.errorCount > 0
            ? `${number(summary.validCount + summary.warningCount)} accepted, ${number(summary.errorCount)} held as ` +
              `${summary.errorCount === 1 ? 'an exception' : 'exceptions'} with the reason. Nothing has been discarded.`
            : // A creditor file or a chart of accounts has no monetary total, and
              // quoting "£0.00" against one implies a figure that was never in play.
              b.declared_control_total_minor !== null || summary.computedControlTotalMinor !== 0
              ? `Every row was accepted. Computed control total ${money(summary.computedControlTotalMinor)}.`
              : 'Every row was accepted.',
      });
      refresh();
    });

  const doDryRun = (b: Batch) =>
    void dryRun.run(b.id).then((result) => {
      if (!result) return;
      setNotice({
        tone: result.blockers.length > 0 ? 'warn' : 'ok',
        title: `Dry run of ${b.reference}`,
        body:
          `${number(result.wouldCreate)} records would be created, ${number(result.wouldUpdate)} updated and ` +
          `${number(result.wouldSkip)} left out. Nothing has been changed.`,
      });
      refresh();
    });

  const doApprove = (b: Batch) =>
    void approve.run(b.id).then((result) => {
      if (!result) return;
      setNotice({
        tone: 'ok',
        title: `${result.reference} approved for import`,
        body: 'The batch may now be imported. Every created record will be linked back to the source row it came from.',
      });
      refresh();
    });

  const doImport = (b: Batch) =>
    void runImport.run(b.id).then((result) => {
      if (!result) return;
      setConfirmImport(null);
      setNotice({
        tone: 'ok',
        title: `${result.reference} imported`,
        body:
          `${number(result.imported)} records created. ${number(result.skipped)} source rows were not imported and ` +
          'remain in the batch with the reason.',
      });
      refresh();
    });

  /* ---------------------------------------------------------------- *
   * Column mapping
   * ---------------------------------------------------------------- */

  const mappingFor = (b: Batch): Record<string, string> =>
    draftMapping && draftMapping.batchId === b.id ? draftMapping.mapping : b.field_mapping;

  const setColumnFor = (b: Batch, fieldKey: string, column: string) => {
    const next = { ...mappingFor(b) };
    // An unmapped field is absent from the mapping, never present and empty:
    // the server looks the column up by name, and "" matches no column.
    if (column) next[fieldKey] = column;
    else delete next[fieldKey];
    setDraftMapping({ batchId: b.id, mapping: next });
  };

  const mappingChanged = (b: Batch): boolean => {
    const draft = mappingFor(b);
    const saved = b.field_mapping;
    const keys = Object.keys(draft);
    return keys.length !== Object.keys(saved).length || keys.some((k) => draft[k] !== saved[k]);
  };

  const doSaveMapping = (b: Batch) =>
    void saveMapping.run(b.id, mappingFor(b)).then((result) => {
      if (!result) return;
      setDraftMapping(null);
      setNotice({
        tone: 'info',
        title: `Mapping saved for ${b.reference}`,
        body: 'Validate the batch again so every row is re-checked against the columns you have just chosen.',
      });
      refresh();
    });

  /* ---------------------------------------------------------------- *
   * Upload
   * ---------------------------------------------------------------- */

  const declaredTotalError =
    uploadForm.declaredControlTotal.trim() === '' ||
    /^-?\d+(\.\d{1,2})?$/.test(uploadForm.declaredControlTotal.replace(/[£,\s]/g, ''))
      ? null
      : 'Enter the amount in pounds and pence, for example 1,142,755.00.';

  const uploadReady =
    file !== null &&
    uploadForm.name.trim().length >= 3 &&
    uploadForm.sourceSystem.trim().length >= 2 &&
    uploadForm.dataDomain !== '' &&
    declaredTotalError === null;

  const closeUpload = () => {
    setUploadOpen(false);
    setUploadForm(EMPTY_UPLOAD);
    setFile(null);
    upload.clearError();
  };

  const submitUpload = () => {
    if (!file || !uploadReady) return;
    const body = new FormData();
    body.append('file', file);
    body.append('name', uploadForm.name.trim());
    body.append('sourceSystem', uploadForm.sourceSystem.trim());
    body.append('dataDomain', uploadForm.dataDomain);
    if (uploadForm.declaredControlTotal.trim()) {
      body.append('declaredControlTotal', uploadForm.declaredControlTotal.trim());
    }
    if (uploadForm.declaredRecordCount.trim()) {
      body.append('declaredRecordCount', uploadForm.declaredRecordCount.trim());
    }
    if (uploadForm.notes.trim()) body.append('notes', uploadForm.notes.trim());

    void upload.run(body).then((result) => {
      if (!result) return;
      const missing = result.unmappedFields.filter((f) => f.required);
      closeUpload();
      batches.reload();
      select(result.batchId);
      setNotice({
        tone: missing.length > 0 ? 'warn' : 'info',
        title: `${result.reference} created from ${result.headers.length} columns and ${number(result.rowCount)} rows`,
        body:
          missing.length > 0
            ? `Nothing has been imported. ${missing.map((f) => f.label).join(', ')} could not be matched to a column — ` +
              'choose the right column under Column mapping, then validate.'
            : 'Nothing has been imported. Every column was matched automatically — check the mapping, then validate.',
      });
    });
  };

  /* ---------------------------------------------------------------- *
   * Presentation helpers that need the tenant's vocabulary
   * ---------------------------------------------------------------- */

  /**
   * What a domain is called here.
   *
   * Five of the twelve are nouns the tenant names for itself, so they come from
   * the terminology rather than from the server's English defaults; the rest are
   * ordinary accounting words and are taken as supplied.
   */
  const domainLabel = (key: string): string => {
    const fromTerms: Record<string, string> = {
      cost_centres: t.costCentrePlural,
      suppliers: t.supplierPlural,
      customers: t.customerPlural,
      purchase_orders: t.purchaseOrderPlural,
      projects: t.projectPlural,
    };
    return fromTerms[key] ?? domains.data?.domains.find((d) => d.key === key)?.label ?? statusLabel(key);
  };

  /** A target field's name in the words its own specification uses. */
  const fieldLabel = (fields: FieldSpec[], key: string | null): string => {
    if (!key) return '—';
    const spec = fields.find((f) => f.key === key);
    return spec ? spec.label : humanise(key.replace(/([a-z0-9])([A-Z])/g, '$1 $2').toLowerCase());
  };

  const totalRows = list.reduce((sum, b) => sum + b.row_count, 0);
  const totalExceptions = list.reduce((sum, b) => sum + b.error_count, 0);

  const uploadButton = domains.data ? (
    <button type="button" className="btn btn--primary" onClick={() => setUploadOpen(true)}>
      Upload an extract
    </button>
  ) : null;

  return (
    <>
      <PageHeader
        title="Migration centre"
        subtitle="Upload, inspect, map, validate, dry run, reconcile, approve, import."
        meta={
          list.length > 0 ? (
            <>
              <span>
                {number(list.length)} {list.length === 1 ? 'batch' : 'batches'}
              </span>
              <span>{number(totalRows)} rows received</span>
              <span>
                {number(totalExceptions)} held as {totalExceptions === 1 ? 'an exception' : 'exceptions'}
              </span>
            </>
          ) : undefined
        }
        actions={uploadButton}
      />

      <div className="stack">
        <Alert tone="info" title="What this guarantees">
          <p>
            Every row is accounted for. A row that cannot be imported is held as an exception with the reason and the
            original data, never dropped. A batch whose control totals do not agree with the source system's own figures
            cannot be approved, and the person who uploaded a batch cannot be the person who approves it.
          </p>
        </Alert>

        {notice ? (
          <Alert tone={notice.tone} title={notice.title}>
            <p>{notice.body}</p>
          </Alert>
        ) : null}

        <ErrorNotice error={validate.error ?? dryRun.error ?? approve.error ?? runImport.error ?? saveMapping.error} />

        <Card title="Batches" subtitle="Every extract received, and where each one has reached." flush>
          <AsyncPanel state={batches}>
            {(data) =>
              data.batches.length === 0 ? (
                <EmptyState title="No extract has been received yet" icon="⇥">
                  <p>
                    A migration begins with a CSV extract from the system being replaced. Upload it here and it is held,
                    row for row, until you have seen what it contains and agreed the totals — nothing reaches the ledger
                    until a second person approves it.
                  </p>
                  {uploadButton ? <p style={{ marginTop: '0.75rem' }}>{uploadButton}</p> : null}
                </EmptyState>
              ) : (
                <DataTable
                  caption="Migration batches"
                  rows={data.batches}
                  rowKey={(b) => b.id}
                  onRowClick={(b) => select(b.id)}
                  /* A cut-over produces dozens of extracts; the search box and pager
                     earn their space only once the list is past reading at a glance. */
                  searchValue={
                    data.batches.length > 8
                      ? (b) => `${b.reference} ${b.name} ${b.source_system} ${domainLabel(b.data_domain)}`
                      : undefined
                  }
                  searchLabel="Search the batches"
                  pageSize={data.batches.length > 12 ? 12 : undefined}
                  columns={[
                    {
                      key: 'ref',
                      header: 'Batch',
                      width: '15rem',
                      sortValue: (b) => b.reference,
                      render: (b) => (
                        <>
                          <span className="mono">{b.reference}</span>
                          <div className="smaller muted">{b.name}</div>
                        </>
                      ),
                    },
                    {
                      key: 'domain',
                      header: 'Data',
                      sortValue: (b) => domainLabel(b.data_domain),
                      render: (b) => (
                        <>
                          <div>{domainLabel(b.data_domain)}</div>
                          <div className="smaller muted">{b.source_system}</div>
                        </>
                      ),
                    },
                    {
                      key: 'rows',
                      header: 'Rows',
                      numeric: true,
                      sortValue: (b) => b.row_count,
                      render: (b) => number(b.row_count),
                    },
                    {
                      key: 'accepted',
                      header: 'Accepted',
                      numeric: true,
                      sortValue: (b) => b.valid_count + b.warning_count,
                      render: (b) =>
                        b.imported_at ? (
                          <>
                            {number(b.imported_count)}
                            <div className="smaller muted">imported</div>
                          </>
                        ) : hasBeenValidated(b) ? (
                          // Rows carrying a warning still import; the count of what
                          // would go in is valid plus warning, both server figures.
                          number(b.valid_count + b.warning_count)
                        ) : (
                          <span className="muted">—</span>
                        ),
                    },
                    {
                      key: 'exceptions',
                      header: 'Exceptions',
                      numeric: true,
                      sortValue: (b) => b.error_count,
                      render: (b) =>
                        !hasBeenValidated(b) ? (
                          <span className="muted">—</span>
                        ) : b.error_count > 0 ? (
                          <Badge tone="warn">{number(b.error_count)}</Badge>
                        ) : (
                          <span className="muted">None</span>
                        ),
                    },
                    {
                      key: 'totals',
                      header: 'Control total',
                      numeric: true,
                      sortValue: (b) => asMinor(b.declared_control_total_minor),
                      render: (b) =>
                        b.declared_control_total_minor === null ? (
                          <span className="muted smaller">None declared</span>
                        ) : (
                          <>
                            <Money minor={asMinor(b.declared_control_total_minor)} />
                            {hasBeenValidated(b) && !b.reconciled ? (
                              <div className="smaller num--negative">
                                computed {money(asMinor(b.computed_control_total_minor))}
                              </div>
                            ) : null}
                          </>
                        ),
                    },
                    {
                      key: 'people',
                      header: 'Uploaded by',
                      render: (b) => (
                        <>
                          <div className="smaller">{b.created_by_name ?? 'Not recorded'}</div>
                          <div className="smaller muted">
                            {b.approved_by_name ? `Approved by ${b.approved_by_name}` : 'Not yet approved'}
                          </div>
                        </>
                      ),
                    },
                    {
                      key: 'status',
                      header: 'Status',
                      sortValue: (b) => STEP_REACHED[b.status] ?? 0,
                      render: (b) => <Badge tone={statusTone(b.status)}>{statusLabel(b.status)}</Badge>,
                    },
                  ]}
                />
              )
            }
          </AsyncPanel>
        </Card>

        <div className="grid grid--3">
          <div style={{ gridColumn: 'span 2' }} className="stack">
            {selected ? (
              <AsyncPanel state={detail}>
                {(data) => {
                  if (!data) return null;
                  const b = data.batch;
                  const reached = STEP_REACHED[b.status] ?? 0;
                  const settled = SETTLED.includes(b.status);
                  const blockers = settled ? [] : approvalBlockers(b);
                  const uploadedByMe = user !== null && b.created_by === user.id;
                  const ready = !settled && blockers.length === 0;
                  const mapping = mappingFor(b);
                  const tab = tabOverride ?? (b.error_count > 0 ? 'exceptions' : 'rows');
                  const declared = data.reconciliation.declaredMinor;

                  // Each step is drawn only where the server would accept it, so a
                  // blocked batch shows the reasons and no button at all rather than a
                  // button that answers back.
                  const showValidate = AWAITING_VALIDATION.includes(b.status);
                  const showDryRun = b.status === 'validated';
                  // The server would accept an approval straight after validation, but
                  // the dry run comes first in the published order, so the screen asks
                  // for one thing at a time.
                  const showApprove = ready && !showDryRun && can('migration.approve') && !uploadedByMe;
                  const showImport = b.status === 'approved' && can('migration.manage');
                  const anyAction = showValidate || showDryRun || showApprove || showImport;

                  // Row outcomes left over from a check made before the mapping changed.
                  const staleOutcomes =
                    !hasBeenValidated(b) && data.rowOutcomes.some((o) => o.status !== 'pending');

                  return (
                    <>
                      <Card
                        title={`${b.reference} — ${b.name}`}
                        subtitle={`${domainLabel(b.data_domain)} from ${b.source_system} · step ${reached} of 8`}
                        actions={
                          anyAction ? (
                            <>
                              {showValidate ? (
                                <button
                                  type="button"
                                  className="btn btn--primary"
                                  disabled={validate.busy}
                                  onClick={() => doValidate(b)}
                                >
                                  {validate.busy ? 'Checking every row…' : 'Validate every row'}
                                </button>
                              ) : null}

                              {showDryRun ? (
                                <button
                                  type="button"
                                  className="btn btn--primary"
                                  disabled={dryRun.busy}
                                  onClick={() => doDryRun(b)}
                                >
                                  {dryRun.busy ? 'Working out what would change…' : 'Dry run'}
                                </button>
                              ) : null}

                              {showApprove ? (
                                <button
                                  type="button"
                                  className="btn btn--primary"
                                  disabled={approve.busy}
                                  onClick={() => doApprove(b)}
                                >
                                  {approve.busy ? 'Approving…' : 'Approve for import'}
                                </button>
                              ) : null}

                              {showImport ? (
                                <button type="button" className="btn btn--primary" onClick={() => setConfirmImport(b)}>
                                  Import into the ledger
                                </button>
                              ) : null}
                            </>
                          ) : undefined
                        }
                      >
                        <div className="stack">
                          <DefinitionList
                            items={[
                              ['File received', <span className="mono smaller">{b.filename ?? '—'}</span>],
                              ['Uploaded', `${dateTime(b.created_at)} by ${b.created_by_name ?? 'an unnamed user'}`],
                              [
                                'Approved',
                                b.approved_at
                                  ? `${dateTime(b.approved_at)} by ${b.approved_by_name ?? 'an unnamed user'}`
                                  : 'Not yet approved',
                              ],
                              [
                                'Imported',
                                b.imported_at ? dateTime(b.imported_at) : 'Not yet imported — nothing has reached the ledger',
                              ],
                              ...(b.declared_record_count !== null
                                ? ([['Records declared', number(b.declared_record_count)]] as Array<[ReactNode, ReactNode]>)
                                : []),
                              ...(b.notes ? ([['Note', b.notes]] as Array<[ReactNode, ReactNode]>) : []),
                            ]}
                          />

                          {blockers.length > 0 ? (
                            <Alert tone="warn" title="This batch cannot be approved yet">
                              <ul style={{ margin: '0.25rem 0 0', paddingLeft: '1.1rem' }}>
                                {blockers.map((reason) => (
                                  <li key={reason}>{reason}</li>
                                ))}
                              </ul>
                            </Alert>
                          ) : null}

                          {/* The server would take an approval as soon as a batch
                              validates, but the dry run is the step in between and is
                              what the screen should be asking for, so the approval
                              notes hold back until it has been made. */}
                          {ready && !showDryRun && uploadedByMe ? (
                            <Alert tone="info" title="Waiting for a second person">
                              <p>
                                You uploaded this batch, so you cannot also approve it. Migration into the live ledger
                                needs a second person — that separation is the control, and the server enforces it.
                              </p>
                            </Alert>
                          ) : null}

                          {ready && !showDryRun && !uploadedByMe && !can('migration.approve') ? (
                            <Alert tone="info" title="Ready for approval">
                              <p>
                                Everything checks out and the totals agree. Approval rests with someone authorised to
                                accept a migration into the live ledger.
                              </p>
                            </Alert>
                          ) : null}

                          {b.status === 'imported' ? (
                            <Alert tone="ok" title="Imported">
                              <p>
                                {number(b.imported_count)} records were created in the ledger on{' '}
                                {dateTime(b.imported_at)}. Each one carries a link back to the source row it came from.
                              </p>
                            </Alert>
                          ) : null}
                        </div>
                      </Card>

                      <div className="grid grid--4">
                        <Stat
                          label="Rows received"
                          value={number(b.row_count)}
                          detail={`As supplied by ${b.source_system}`}
                        />
                        <Stat
                          label="Accepted"
                          value={hasBeenValidated(b) ? number(b.valid_count + b.warning_count) : '—'}
                          detail={
                            hasBeenValidated(b)
                              ? b.warning_count > 0
                                ? `${number(b.warning_count)} of them with a warning`
                                : 'Every one without a query'
                              : 'Not yet validated'
                          }
                        />
                        <Stat
                          label="Held as exceptions"
                          value={hasBeenValidated(b) ? number(b.error_count) : '—'}
                          tone={b.error_count > 0 ? 'negative' : undefined}
                          detail={
                            b.error_count > 0
                              ? 'Kept with the reason, listed below'
                              : hasBeenValidated(b)
                                ? 'Nothing was held back'
                                : 'Not yet validated'
                          }
                        />
                        <Stat
                          label="Imported"
                          value={number(b.imported_count)}
                          detail={b.imported_at ? dateTime(b.imported_at) : 'Nothing has reached the ledger'}
                        />
                      </div>

                      <Card
                        title="Reconciliation"
                        subtitle="The source system's own figures against what we found in the file."
                        footer={<span className="smaller muted">{data.statement}</span>}
                      >
                        <div className="stack">
                          {declared === null ? (
                            /* A creditor or debtor master file has no monetary total.
                               Three tiles reading £0.00 would imply a reconciliation was
                               done and came to nothing, which is not what happened. */
                            <p className="muted" style={{ margin: 0 }}>
                              This extract carries no monetary total, so there is no control total to agree and the
                              batch may be approved without one. Every row is still accounted for, and the record count
                              below is checked.
                            </p>
                          ) : (
                            <div className="grid grid--3">
                              <Stat
                                label="Declared by the source"
                                value={money(declared)}
                                detail={`From ${b.source_system}, before anything was moved`}
                              />
                              <Stat
                                label="Computed from the rows"
                                value={money(data.reconciliation.computedMinor)}
                                detail={
                                  hasBeenValidated(b)
                                    ? 'Summed from the rows that were accepted'
                                    : 'The rows have not been validated yet'
                                }
                              />
                              <Stat
                                label="Difference"
                                value={money(data.reconciliation.differenceMinor)}
                                tone={data.reconciliation.reconciled ? 'positive' : 'negative'}
                                detail={
                                  data.reconciliation.reconciled
                                    ? 'Reconciles — the batch may be approved'
                                    : 'Does not reconcile — the batch cannot be approved'
                                }
                              />
                            </div>
                          )}

                          {/* The volume check. The server treats a short file as
                              something to explain rather than something to refuse, so
                              it is reported here in the same terms. */}
                          {b.declared_record_count !== null ? (
                            b.declared_record_count === b.row_count ? (
                              <Alert tone="ok" title="The record count agrees">
                                <p>
                                  {number(b.row_count)} rows were received and {number(b.declared_record_count)} were
                                  declared by {b.source_system}. Nothing is missing from the file.
                                </p>
                              </Alert>
                            ) : (
                              <Alert tone="warn" title="The record count does not agree">
                                <p>
                                  The file contains {number(b.row_count)} rows but {number(b.declared_record_count)}{' '}
                                  were declared by {b.source_system}. Confirm the extract is complete before importing
                                  it.
                                </p>
                              </Alert>
                            )
                          ) : null}
                        </div>
                      </Card>

                      <Card
                        title="Every row is accounted for"
                        /* Changing the mapping puts the batch back to 'mapped' but leaves
                           the row outcomes from the previous check in place. Saying so is
                           better than showing a row marked accepted beside a batch the
                           rest of the screen calls unvalidated. */
                        subtitle={
                          staleOutcomes
                            ? `${outcomeSummary(data.rowOutcomes)} — from the last check, before the mapping was changed`
                            : outcomeSummary(data.rowOutcomes)
                        }
                        flush
                      >
                        <div style={{ padding: '0.75rem 1.125rem 0' }}>
                          <Tabs
                            label="Choose what to look at"
                            active={tab}
                            onChange={setTabOverride}
                            tabs={[
                              { key: 'exceptions', label: 'Exceptions', badge: b.error_count },
                              { key: 'rows', label: 'Every row' },
                              { key: 'mapping', label: 'Column mapping' },
                            ]}
                          />
                        </div>

                        {tab === 'exceptions' ? (
                          data.exceptions.length === 0 ? (
                            <EmptyState
                              title={hasBeenValidated(b) ? 'No row was held back' : 'The rows have not been validated yet'}
                              /* A tick belongs on a check that has been made, not on one
                                 that is still to come. */
                              icon={hasBeenValidated(b) ? '✓' : '—'}
                            >
                              <p>
                                {hasBeenValidated(b)
                                  ? 'Every row in this extract could be read and matched to the target fields.'
                                  : 'Validate the batch and anything that cannot be imported will be listed here with its reason.'}
                              </p>
                            </EmptyState>
                          ) : (
                            <>
                              {b.error_count > data.exceptions.length ? (
                                <p className="smaller muted" style={{ padding: '0.75rem 1.125rem 0' }}>
                                  Showing the first {number(data.exceptions.length)} of {number(b.error_count)}. Every one
                                  of them is retained in the batch.
                                </p>
                              ) : null}
                              <DataTable
                                caption="Rows that could not be imported"
                                rows={data.exceptions}
                                rowKey={(e) => String(e.row_number)}
                                emptyMessage="No row was held back."
                                columns={[
                                  {
                                    key: 'row',
                                    header: 'Row',
                                    numeric: true,
                                    width: '5rem',
                                    sortValue: (e) => e.row_number,
                                    render: (e) => e.row_number,
                                  },
                                  {
                                    key: 'field',
                                    header: 'Field',
                                    width: '12rem',
                                    render: (e) => (
                                      <>
                                        {e.findings.map((f, i) => (
                                          <div key={i} className="smaller">
                                            {fieldLabel(data.targetFields, f.field)}
                                          </div>
                                        ))}
                                      </>
                                    ),
                                  },
                                  {
                                    key: 'why',
                                    header: 'Why it was held',
                                    render: (e) => (
                                      <>
                                        {e.findings.map((f, i) => (
                                          <div key={i}>{f.message}</div>
                                        ))}
                                      </>
                                    ),
                                  },
                                  {
                                    key: 'raw',
                                    header: 'What arrived',
                                    width: '12rem',
                                    render: (e) => (
                                      <details>
                                        <summary className="smaller">Show the row as supplied</summary>
                                        {/* Deliberately unformatted. The point of the
                                            disclosure is what the old system actually
                                            sent, so tidying it up would hide the reason
                                            the row failed. */}
                                        <pre className="smaller mono" style={{ whiteSpace: 'pre', margin: '0.5rem 0 0' }}>
                                          {rowAsSupplied(b.detected_columns, e.raw_data)}
                                        </pre>
                                      </details>
                                    ),
                                  },
                                ]}
                              />
                            </>
                          )
                        ) : null}

                        {tab === 'rows' ? (
                          <>
                            {b.row_count > data.rows.length ? (
                              <p className="smaller muted" style={{ padding: '0.75rem 1.125rem 0' }}>
                                Showing {number(data.rows.length)} of {number(b.row_count)} rows, errors first. All of them
                                are retained in the batch.
                              </p>
                            ) : null}
                            <DataTable
                              caption="Every source row as supplied"
                              rows={data.rows}
                              rowKey={(r) => String(r.row_number)}
                              emptyMessage="This extract contains no rows."
                              searchValue={(r) => `${r.row_number} ${Object.values(r.raw_data).join(' ')}`}
                              searchLabel="Search the rows as supplied"
                              pageSize={15}
                              columns={[
                                {
                                  key: 'row',
                                  header: 'Row',
                                  numeric: true,
                                  width: '5rem',
                                  sortValue: (r) => r.row_number,
                                  render: (r) => r.row_number,
                                },
                                {
                                  key: 'outcome',
                                  header: 'Outcome',
                                  width: '11rem',
                                  sortValue: (r) => rowOutcome(r.status).label,
                                  render: (r) => (
                                    <Badge tone={rowOutcome(r.status).tone}>{rowOutcome(r.status).label}</Badge>
                                  ),
                                },
                                ...b.detected_columns.map((column) => ({
                                  key: `col:${column}`,
                                  header: column,
                                  render: (r: SourceRow) => {
                                    const value = r.raw_data[column];
                                    return value === null || value === undefined || value === '' ? (
                                      <span className="muted">(empty)</span>
                                    ) : (
                                      <span className="smaller">{String(value)}</span>
                                    );
                                  },
                                })),
                              ]}
                            />
                          </>
                        ) : null}

                        {tab === 'mapping' ? (
                          <div style={{ padding: '1rem 1.125rem' }} className="stack">
                            <p className="smaller muted" style={{ margin: 0 }}>
                              Which column of the extract feeds which field here. Common column names were matched
                              automatically on arrival; every match is shown so it can be corrected before anything is
                              validated.
                            </p>

                            {settled ? (
                              <DefinitionList
                                items={data.targetFields.map((f): [ReactNode, ReactNode] => [
                                  <>
                                    {f.label}
                                    {f.required ? <RequiredMark /> : null}
                                  </>,
                                  mapping[f.key] ? (
                                    <span className="mono smaller">{mapping[f.key]}</span>
                                  ) : (
                                    <span className="muted smaller">Not mapped</span>
                                  ),
                                ])}
                              />
                            ) : (
                              <>
                                <div className="grid grid--2">
                                  {data.targetFields.map((f) => (
                                    <Select
                                      key={f.key}
                                      label={f.label}
                                      required={f.required}
                                      hint={f.description}
                                      error={f.required && !mapping[f.key] ? 'This field must be mapped.' : null}
                                      value={mapping[f.key] ?? ''}
                                      onChange={(e) => setColumnFor(b, f.key, e.currentTarget.value)}
                                    >
                                      <option value="">Not mapped</option>
                                      {b.detected_columns.map((column) => (
                                        <option key={column} value={column}>
                                          {column}
                                        </option>
                                      ))}
                                    </Select>
                                  ))}
                                </div>

                                {/* Nothing to save until something has actually changed, so
                                    neither button is drawn until then. */}
                                {mappingChanged(b) ? (
                                  <div className="row row--end">
                                    <button
                                      type="button"
                                      className="btn"
                                      onClick={() => setDraftMapping(null)}
                                      disabled={saveMapping.busy}
                                    >
                                      Discard the changes
                                    </button>
                                    <button
                                      type="button"
                                      className="btn btn--primary"
                                      disabled={saveMapping.busy}
                                      onClick={() => doSaveMapping(b)}
                                    >
                                      {saveMapping.busy ? 'Saving…' : 'Save the mapping'}
                                    </button>
                                  </div>
                                ) : null}
                              </>
                            )}
                          </div>
                        ) : null}
                      </Card>
                    </>
                  );
                }}
              </AsyncPanel>
            ) : null}
          </div>

          <div className="stack">
            <Card title="How a migration runs" subtitle="Eight steps, in this order, every time.">
              <AsyncPanel state={domains}>
                {(data) => {
                  const current = list.find((b) => b.id === selected);
                  const reached = current ? (STEP_REACHED[current.status] ?? 0) : null;
                  return (
                    <ol className="timeline">
                      {data.workflow.map((step) => {
                        const done = reached !== null && step.step <= reached;
                        const next = reached !== null && step.step === reached + 1;
                        return (
                          <li key={step.step} className={done ? 'timeline__item timeline__item--ok' : 'timeline__item'}>
                            <div className="timeline__time">
                              Step {step.step}
                              {done ? ' · done' : next ? ' · next' : ''}
                            </div>
                            <div className="timeline__title">{step.name}</div>
                            <div className="timeline__detail">{step.description}</div>
                          </li>
                        );
                      })}
                    </ol>
                  );
                }}
              </AsyncPanel>
            </Card>

            <Card title="What can be migrated" subtitle="Each domain has a documented field list and alias matching.">
              <AsyncPanel state={domains}>
                {(data) => (
                  <ul className="stack stack--sm" style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                    {data.domains.map((d) => {
                      const required = d.fields.filter((f) => f.required).length;
                      return (
                        <li key={d.key}>
                          <div className="strong">{domainLabel(d.key)}</div>
                          <div className="smaller muted">{d.description}</div>
                          <details>
                            <summary className="smaller">
                              {number(required)} required field{required === 1 ? '' : 's'} of {number(d.fields.length)}
                            </summary>
                            <ul className="smaller" style={{ margin: '0.35rem 0 0', paddingLeft: '1.1rem' }}>
                              {d.fields.map((f) => (
                                <li key={f.key}>
                                  {f.label}
                                  {f.required ? <RequiredMark /> : null}
                                </li>
                              ))}
                            </ul>
                          </details>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </AsyncPanel>
            </Card>
          </div>
        </div>
      </div>

      <Modal
        open={uploadOpen}
        onClose={closeUpload}
        title="Upload an extract"
        wide
        footer={
          <>
            <button type="button" className="btn" onClick={closeUpload}>
              Cancel
            </button>
            <button
              type="button"
              className="btn btn--primary"
              disabled={!uploadReady || upload.busy}
              onClick={submitUpload}
            >
              {upload.busy ? 'Uploading…' : 'Upload and inspect'}
            </button>
          </>
        }
      >
        <div className="stack">
          <p className="muted" style={{ margin: 0 }}>
            The file is stored exactly as supplied and every row in it is kept, whatever happens to it afterwards.
            Uploading changes nothing in the ledger: the batch has to be mapped, validated, dry run, reconciled and
            approved by a second person before a single record is created.
          </p>

          <ErrorNotice error={upload.error} />

          <TextInput
            label="The extract file"
            required
            type="file"
            accept=".csv,text/csv"
            hint="A CSV export from the system being replaced, with its column names on the first line."
            onChange={(e) => setFile(e.currentTarget.files?.[0] ?? null)}
          />

          <div className="grid grid--2">
            <TextInput
              label="What this batch is"
              required
              value={uploadForm.name}
              hint="How this extract will be referred to. For example: Fixed asset register at cut-over."
              onChange={(e) => setUploadForm({ ...uploadForm, name: e.currentTarget.value })}
            />
            <TextInput
              label="Source system"
              required
              value={uploadForm.sourceSystem}
              hint="The system the extract came from. Two systems can feed the same target."
              onChange={(e) => setUploadForm({ ...uploadForm, sourceSystem: e.currentTarget.value })}
            />
          </div>

          <Select
            label="What kind of data this is"
            required
            value={uploadForm.dataDomain}
            hint="This decides the target fields, the validation rules and how the control total is computed."
            onChange={(e) => setUploadForm({ ...uploadForm, dataDomain: e.currentTarget.value })}
          >
            <option value="">Choose…</option>
            {(domains.data?.domains ?? []).map((d) => (
              <option key={d.key} value={d.key}>
                {domainLabel(d.key)}
              </option>
            ))}
          </Select>

          <div className="grid grid--2">
            <TextInput
              label="Control total declared by the source system"
              value={uploadForm.declaredControlTotal}
              error={declaredTotalError}
              inputMode="decimal"
              hint="The figure the old system reports for this extract. Leave it blank where the data carries no monetary total."
              onChange={(e) => setUploadForm({ ...uploadForm, declaredControlTotal: e.currentTarget.value })}
            />
            <TextInput
              label="Number of records declared"
              value={uploadForm.declaredRecordCount}
              inputMode="numeric"
              hint="Optional. The record count the old system reports, so a short file is obvious straight away."
              onChange={(e) => setUploadForm({ ...uploadForm, declaredRecordCount: e.currentTarget.value })}
            />
          </div>

          <TextArea
            label="Notes"
            rows={3}
            value={uploadForm.notes}
            hint="Optional. Who supplied the extract, when it was taken, anything known about it."
            onChange={(e) => setUploadForm({ ...uploadForm, notes: e.currentTarget.value })}
          />
        </div>
      </Modal>

      <Modal
        open={confirmImport !== null}
        onClose={() => setConfirmImport(null)}
        title="Import this batch into the ledger"
        footer={
          <>
            <button type="button" className="btn" onClick={() => setConfirmImport(null)}>
              Cancel
            </button>
            <button
              type="button"
              className="btn btn--primary"
              disabled={runImport.busy}
              onClick={() => confirmImport && doImport(confirmImport)}
            >
              {runImport.busy ? 'Importing…' : 'Import now'}
            </button>
          </>
        }
      >
        {confirmImport ? (
          <>
            <p className="muted">
              {confirmImport.reference} — {domainLabel(confirmImport.data_domain)} from {confirmImport.source_system} —
              will create{' '}
              <strong>{number(confirmImport.valid_count + confirmImport.warning_count)} records</strong> in the live
              ledger for this {lower(t.entity)}. Every one of them is linked back to the source row it came from, and
              the whole batch is applied in a single transaction: if any part of it fails, none of it is left behind.
            </p>
            {confirmImport.error_count > 0 ? (
              <p className="muted">
                The {number(confirmImport.error_count)} rows held as exceptions are not imported. They stay in this
                batch, with their reasons, exactly as they arrived.
              </p>
            ) : null}
            <ErrorNotice error={runImport.error} />
          </>
        ) : null}
      </Modal>
    </>
  );
}
